import { CELLARS, COLOSSUS, CORVIN, SIMA_BOSS } from '@somnhold/content';
import { iatan2, withinRange } from '../math/fixed.ts';
import type { BattleState } from '../types.ts';
import { insideMark } from './dungeon.ts';
import { inLane } from './fin.ts';
import { onSegment } from './sima.ts';
import { inSimaCone } from './sima-bosses.ts';
import { inBreath } from './sunken.ts';
import { inCone } from './unsleeping/kit.ts';

/** A marked blow still to land: the tick it lands and whether it catches someone standing at a point. */
export interface Blow {
  at: number;
  hits: (x: number, y: number) => boolean;
}

/**
 * Every marked blow that lands within `ticks` (the warning on the floor, then the hit on whoever stands in it),
 * each with the same test of where it hits as the code that lands it. Only read, never changes the state: the
 * client uses it to show my hero where the room has it when a blow comes (apps/client/src/game/predict.ts).
 * A new marked blow belongs here too, or the drawing ahead of the room makes it look like it hits outside.
 * Left out on purpose: what hits by walking or standing still rather than by place (the titan's shake, the
 * aurochs' charge), lasting ground (lava, ink, shards), and Ragna's whirl, which moves after it lands.
 */
export const blowsSoon = (s: BattleState, ticks: number): Blow[] => {
  const out: Blow[] = [];
  const soon = (at: number): boolean => at >= s.tick && at - s.tick <= ticks;
  const circle = (at: number, x: number, y: number, r: number): void => {
    if (soon(at)) out.push({ at, hits: (px, py) => withinRange(x, y, px, py, r) });
  };
  // The crypt guardians' marks (the Warden's slam, nova and ring).
  for (const m of s.dungeon?.marks ?? []) if (soon(m.at)) out.push({ at: m.at, hits: (x, y) => insideMark(m, x, y) });
  // Marked zones counting down: eruptions (the magma lord, the dragon), cracking glass, storm bolts, the Chasm's
  // strikes, the Unsleeping's blasts and thorns. Each lands as its `warn` runs out.
  for (const z of s.zones) {
    if (z.warn === undefined || z.warn <= 0) continue;
    const at = s.tick + z.warn;
    if (z.kind === 'sima' && z.haz === 'beam') {
      if (soon(at)) out.push({ at, hits: (x, y) => onSegment(x, y, z.x, z.y, z.tx ?? z.x, z.ty ?? z.y, z.radius) });
    } else if (z.kind === 'eruption' || z.kind === 'glass' || z.kind === 'bolt' || z.kind === 'sima' || z.kind === 'unBlast' || z.kind === 'briar') {
      circle(at, z.x, z.y, z.radius);
    }
  }
  // The Chasm's guardians: the spore mother's swell, the bellows' cone after it breathes in.
  const act = s.dungeon?.sima?.act;
  const by = act ? s.enemies.find((e) => e.id === act.by && e.hp > 0) : undefined;
  if (act && by) {
    if (act.kind === 'swell') circle(act.until, by.x, by.y, SIMA_BOSS.sporeMother.radius);
    if (act.kind === 'inhale' && soon(act.until)) {
      const B = SIMA_BOSS.bellows;
      const dir = iatan2(act.ty - by.y, act.tx - by.x);
      out.push({ at: act.until, hits: (x, y) => inSimaCone(s, by, B.range, B.arc, dir, true, x, y) });
    }
  }
  for (const e of s.enemies) {
    if (e.hp <= 0) continue;
    // The obsidian colossus: its glint and shine, then the slam round it.
    if (e.kind === 'colossus' && s.tick - e.born >= COLOSSUS.every) {
      const k = (s.tick - e.born) % COLOSSUS.every;
      circle(s.tick + ((COLOSSUS.shineTicks - k + COLOSSUS.every) % COLOSSUS.every), e.x, e.y, COLOSSUS.slamRadius);
    }
    // The Unsleeping's channelled last blows: Sir Corvin's cone, the others' marked circles. Ragna's whirl
    // moves after it lands and Bruni's only strikes towers.
    const u = e.un;
    if (!u || u.cast <= 0) continue;
    const at = s.tick + u.cast;
    if (u.id === 'corvin') {
      if (soon(at)) out.push({ at, hits: (x, y) => inCone(e, u.castA, x, y, CORVIN.night.radius, CORVIN.night.half) });
    } else if (u.id !== 'ragna' && u.id !== 'bruni') {
      for (const p of u.spots) circle(at, p.x, p.y, p.r);
    }
  }
  // The fin's lane (the posts hold, but not against the dragon's fire), and the drowned dragon's breath.
  const fr = s.rules?.fin;
  const fin = s.fin;
  if (fr && fin && fin.lane >= 0 && soon(s.tick + fin.next - s.waveT)) {
    const lane = fin.lane;
    out.push({ at: s.tick + fin.next - s.waveT, hits: (x, y) => inLane(s, lane, x, y) && (!!fr.breath || !fr.posts.some((p) => withinRange(x, y, p.x, p.y, fr.hold))) });
  }
  const sk = s.sunken;
  const br = s.rules?.sunken?.boss?.breath;
  const dragon = sk?.id ? s.enemies.find((e) => e.id === sk.id && e.hp > 0) : undefined;
  if (sk && br && dragon && sk.draw && soon(sk.draw)) {
    const aim = sk.aim;
    out.push({ at: sk.draw, hits: (x, y) => inBreath(dragon, aim, br, x, y) });
  }
  // The tail's lash: everyone slides but whoever holds a post.
  const lr = s.rules?.lash;
  const lash = s.lash;
  if (lr && lash && lash.warn > 0) {
    if (soon(s.tick + lash.warn)) out.push({ at: s.tick + lash.warn, hits: (x, y) => !lr.posts.some((p) => withinRange(p.x, p.y, x, y, lr.postR)) });
  }
  // Barrels with a lit fuse: the saga's blast and the cellars' powder.
  const blast = s.rules?.blast;
  for (const b of s.blast?.barrels ?? []) if (blast && b.fuse) circle(b.fuse, b.x, b.y, blast.radius);
  for (const it of s.dungeon?.exp?.items ?? []) if (it.kind === 'barrel' && it.fuse > 0) circle(it.fuse, it.x, it.y, CELLARS.barrel.blastRadius);
  return out;
};
